import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './LoginPage.module.css';

/*
   Two-step passwordless login.

   Step 'email' — collect the address, call POST /auth/request-code, then
   advance unconditionally (the API returns 200 even when the email doesn't
   match a real account; see docs/passwordless-email-code-migration.md).

   Step 'code'  — collect the 6-digit code, call POST /auth/verify-code,
   open the session on success. Two affordances exist on this step:
     - "Utiliser une autre adresse" — clears the code field and goes back.
     - "Renvoyer le code"           — re-calls request-code.

   The code field uses inputMode="numeric" + autoComplete="one-time-code" so
   iOS / Chrome on Android surface the email-detected code as a tap-to-fill
   suggestion above the keyboard.
*/
type Step = 'email' | 'code';

export function LoginPage() {
	const { requestCode, verifyCode } = useAuth();
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>('email');
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [info, setInfo] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSendCode(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setInfo(null);
		setLoading(true);
		try {
			await requestCode(email.trim());
			setStep('code');
			setInfo('Si cette adresse correspond à un compte, un code à 6 chiffres a été envoyé.');
		} catch (err: any) {
			setError(err.message ?? "Erreur lors de l'envoi du code");
		} finally {
			setLoading(false);
		}
	}

	async function handleVerify(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setInfo(null);
		setLoading(true);
		try {
			await verifyCode(email.trim(), code.trim());
			navigate('/');
		} catch (err: any) {
			// 401 invalid_credentials is the only failure shape the SPA needs to handle.
			setError(
				err.message === 'invalid_credentials'
					? 'Code incorrect ou expiré. Demandez un nouveau code si nécessaire.'
					: (err.message ?? 'Échec de la connexion'),
			);
		} finally {
			setLoading(false);
		}
	}

	async function handleResend() {
		setError(null);
		setInfo(null);
		setLoading(true);
		try {
			await requestCode(email.trim());
			setInfo('Nouveau code envoyé.');
		} catch (err: any) {
			setError(err.message ?? "Erreur lors de l'envoi du code");
		} finally {
			setLoading(false);
		}
	}

	function handleChangeEmail() {
		setStep('email');
		setCode('');
		setError(null);
		setInfo(null);
	}

	return (
		<div className={styles.page}>
			<div className={styles.card}>
				<h1 className={styles.title}>Spirtech</h1>
				<p className={styles.sub}>Suivi du temps · connexion par courriel</p>

				{step === 'email' && (
					<form onSubmit={handleSendCode} className={styles.form}>
						<label className={styles.label}>
							Courriel
							<input
								type="email"
								required
								autoFocus
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className={styles.input}
								placeholder="vous@spirtech.com"
								autoComplete="email"
							/>
						</label>
						{error && <p className={styles.error}>{error}</p>}
						<button type="submit" className={styles.btn} disabled={loading || !email}>
							{loading ? 'Envoi…' : 'Recevoir un code par courriel'}
						</button>
					</form>
				)}

				{step === 'code' && (
					<form onSubmit={handleVerify} className={styles.form}>
						<p className={styles.sub} style={{ margin: 0 }}>
							Saisissez le code à 6 chiffres reçu sur <strong>{email}</strong>.
						</p>
						<label className={styles.label}>
							Code à 6 chiffres
							<input
								type="text"
								required
								autoFocus
								value={code}
								onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
								className={styles.input}
								placeholder="123456"
								inputMode="numeric"
								autoComplete="one-time-code"
								pattern="\d{6}"
								maxLength={6}
							/>
						</label>
						{info && (
							<p className={styles.info ?? ''} style={{ color: '#65a30d', margin: 0 }}>
								{info}
							</p>
						)}
						{error && <p className={styles.error}>{error}</p>}
						<button type="submit" className={styles.btn} disabled={loading || code.length !== 6}>
							{loading ? 'Connexion…' : 'Se connecter'}
						</button>
						<div
							className={styles.linksRow ?? ''}
							style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}
						>
							<button
								type="button"
								className={styles.linkBtn ?? ''}
								style={btnLink}
								onClick={handleChangeEmail}
								disabled={loading}
							>
								Utiliser une autre adresse
							</button>
							<button
								type="button"
								className={styles.linkBtn ?? ''}
								style={btnLink}
								onClick={handleResend}
								disabled={loading}
							>
								Renvoyer le code
							</button>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}

const btnLink: React.CSSProperties = {
	background: 'transparent',
	border: 'none',
	color: '#0e7490',
	cursor: 'pointer',
	padding: 0,
	fontSize: 14,
	textDecoration: 'underline',
};
